import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmt } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, Trash, WhatsappLogo, Globe, User, MagnifyingGlass, X, Download } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useSelection } from "@/hooks/useSelection";
import { useConfirm } from "@/hooks/useConfirm";
import BulkActionBar, { SelectAllCheckbox, RowCheckbox, runBulkAction, BulkBarButton, BulkBarSelect } from "@/components/BulkActionBar";
import { exportRowsToCsv, todayStamp } from "@/lib/csvExport";

const STATES = [
  { value: "pendiente", label: "Pendiente", color: "#FFCC00", bg: "#FFF9E6" },
  { value: "pagado", label: "Pagado", color: "#00C853", bg: "#E8F5E9" },
  { value: "enviado", label: "Enviado", color: "#1E4FCC", bg: "#E3F2FD" },
  { value: "cancelado", label: "Cancelado", color: "#FF2A00", bg: "#FFEBEE" },
];

export default function Sales() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [sucursales, setSucursales] = useState([]);

  const load = useCallback(async () => {
    const [s, st] = await Promise.all([api.get("/sales"), api.get("/sales/stats")]);
    setSales(s.data);
    setStats(st.data);
  }, []);

  const loadSucursales = useCallback(async () => {
    try {
      const r = await api.get("/sucursales");
      setSucursales(r.data || []);
    } catch {
      // ignore — the dropdown is optional UI
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadSucursales();
  }, [loadSucursales]);

  const update = async (id, estado) => {
    try {
      await api.put(`/sales/${id}`, { estado });
      toast.success("Estado actualizado");
      load();
    } catch (e) {
      toast.error("No se pudo actualizar");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar este pedido?")) return;
    await api.delete(`/sales/${id}`);
    toast.success("Eliminado");
    load();
  };

  const filtered = useMemo(() => {
    const byState = sales.filter((s) => filter === "all" || s.estado === filter);
    const q = query.trim().toLowerCase();
    if (!q) return byState;
    return byState.filter((s) =>
      `${s.product_nombre || ""} ${s.comprador_nombre || ""} ${s.comprador_telefono || ""} ${s.comprador_email || ""} ${s.id || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [sales, filter, query]);

  const selection = useSelection(filtered);
  const [bulkBusy, setBulkBusy] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const onBulkDelete = async () => {
    const ids = selection.selectedIds;
    if (!ids.length) return;
    const ok = await confirm({
      title: `Eliminar ${ids.length} pedido${ids.length === 1 ? "" : "s"}`,
      description: "Se borrarán de la lista de ventas y dejarán de contar para reportes/utilidad. Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    setBulkBusy(true);
    const { ok: done, fail } = await runBulkAction(ids, (id) => api.delete(`/sales/${id}`));
    setBulkBusy(false);
    selection.clear();
    if (done) toast.success(`${done} pedido(s) eliminado(s)`);
    if (fail) toast.error(`${fail} no se pudieron eliminar`);
    load();
  };

  const onBulkStatusChange = async (newEstado) => {
    if (!newEstado) return;
    const ids = selection.selectedIds;
    if (!ids.length) return;
    const stateMeta = STATES.find((x) => x.value === newEstado);
    const ok = await confirm({
      title: `Marcar ${ids.length} pedido${ids.length === 1 ? "" : "s"} como "${stateMeta?.label || newEstado}"`,
      description: "Se actualizará el estado en bloque. Los reportes y utilidad se recalculan automáticamente.",
      confirmLabel: "Cambiar estado",
      tone: "default",
    });
    if (!ok) return;
    setBulkBusy(true);
    const { ok: done, fail } = await runBulkAction(ids, (id) =>
      api.put(`/sales/${id}`, { estado: newEstado }),
    );
    setBulkBusy(false);
    selection.clear();
    if (done) toast.success(`${done} pedido(s) marcado(s) como ${stateMeta?.label || newEstado}`);
    if (fail) toast.error(`${fail} no se pudieron actualizar`);
    load();
  };

  const onBulkReassignSucursal = async (newSucursalId) => {
    if (newSucursalId === "") return;
    const ids = selection.selectedIds;
    if (!ids.length) return;
    const target = newSucursalId === "__none__" ? null : newSucursalId;
    const targetLabel =
      target === null
        ? "Sin sucursal"
        : sucursales.find((s) => s.id === target)?.nombre || "—";
    const ok = await confirm({
      title: `Reasignar ${ids.length} pedido${ids.length === 1 ? "" : "s"} a "${targetLabel}"`,
      description:
        "Los pedidos pasarán a contar en los reportes y filtros de la sucursal destino. Los importes y estados se conservan.",
      confirmLabel: "Reasignar",
      tone: "default",
    });
    if (!ok) return;
    setBulkBusy(true);
    const { ok: done, fail } = await runBulkAction(ids, (id) =>
      api.put(`/sales/${id}`, { sucursal_id: target || "" }),
    );
    setBulkBusy(false);
    selection.clear();
    if (done) toast.success(`${done} pedido(s) reasignado(s) a ${targetLabel}`);
    if (fail) toast.error(`${fail} no se pudieron reasignar`);
    load();
  };

  const onBulkExport = () => {
    const ids = new Set(selection.selectedIds);
    const rows = filtered.filter((s) => ids.has(s.id));
    if (!rows.length) return;
    exportRowsToCsv({
      filename: `ventas-${todayStamp()}.csv`,
      headers: [
        { label: "Folio", value: (s) => s.id },
        { key: "product_nombre", label: "Producto" },
        { key: "comprador_nombre", label: "Cliente" },
        { key: "comprador_telefono", label: "Teléfono" },
        { key: "comprador_email", label: "Email" },
        { key: "canal", label: "Canal" },
        { key: "cantidad", label: "Cantidad" },
        { key: "total", label: "Total" },
        { key: "utilidad", label: "Utilidad" },
        { key: "estado", label: "Estado" },
        { key: "created_at", label: "Fecha" },
      ],
      rows,
    });
    toast.success(`${rows.length} pedido(s) exportado(s) a CSV`);
  };
  const t = stats || { total_ventas: 0, total_utilidad: 0, margen_pct: 0, unidades_vendidas: 0, pedidos_pendientes: 0 };

  return (
    <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-10 max-w-[1400px]" data-testid="sales-page">
      <div className="mb-8 md:mb-10">
        <div className="overline text-klein">Ventas</div>
        <h1 className="h-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-2">
          Pedidos y conversiones.
        </h1>
        <p className="text-muted2 mt-3 max-w-xl">
          Cada compra desde tu link público o WhatsApp aterriza aquí. Cambia el estado para reflejar pagos y envíos.
        </p>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-zinc-200 mb-10">
        <Kpi label="Ventas pagadas" value={fmt(t.total_ventas)} accent />
        <Kpi label="Utilidad real" value={fmt(t.total_utilidad)} positive={t.total_utilidad >= 0} sub={`${t.margen_pct}% margen`} />
        <Kpi label="Unidades" value={t.unidades_vendidas} />
        <Kpi label="Pendientes" value={t.pedidos_pendientes} sub="por confirmar" />
      </section>

      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
        <span className="overline">Filtrar</span>
        <button
          className={`px-2.5 py-1 border ${filter === "all" ? "bg-klein text-white border-klein" : "border-zinc-300 hover:bg-zinc-50"}`}
          onClick={() => setFilter("all")}
          data-testid="filter-all"
        >
          Todos ({sales.length})
        </button>
        {STATES.map((s) => {
          const n = sales.filter((x) => x.estado === s.value).length;
          return (
            <button
              key={s.value}
              className={`px-2.5 py-1 border ${filter === s.value ? "bg-klein text-white border-klein" : "border-zinc-300 hover:bg-zinc-50"}`}
              onClick={() => setFilter(s.value)}
              data-testid={`filter-${s.value}`}
            >
              {s.label} ({n})
            </button>
          );
        })}
      </div>

      {sales.length > 0 && (
        <div className="mb-4 flex items-center gap-2 max-w-lg" data-testid="sales-search-wrap">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2 pointer-events-none" />
            <input
              type="text"
              className="input-flat pl-8"
              placeholder="Buscar por producto, cliente, teléfono, folio…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="sales-search"
              data-help="Filtra las ventas por producto, nombre del cliente, teléfono, email o folio."
            />
          </div>
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="btn-ghost text-xs px-3 py-1.5"
              data-testid="sales-search-clear"
            >
              <X size={14} /> {filtered.length} resultado{filtered.length === 1 ? "" : "s"}
            </button>
          )}
        </div>
      )}

      <BulkActionBar
        count={selection.count}
        noun="pedido"
        pluralNoun="pedidos"
        onDelete={onBulkDelete}
        onClear={selection.clear}
        busy={bulkBusy}
        testid="sales-bulk-bar"
        extraActions={
          <>
            <BulkBarSelect
              value=""
              placeholder="Cambiar estado…"
              options={STATES.map((s) => ({ value: s.value, label: s.label }))}
              onChange={onBulkStatusChange}
              testid="sales-bulk-bar-status"
              disabled={bulkBusy}
            />
            {isAdmin && sucursales.length > 0 && (
              <BulkBarSelect
                value=""
                placeholder="Reasignar sucursal…"
                options={[
                  { value: "__none__", label: "— Sin sucursal —" },
                  ...sucursales.map((s) => ({ value: s.id, label: s.nombre })),
                ]}
                onChange={onBulkReassignSucursal}
                testid="sales-bulk-bar-sucursal"
                disabled={bulkBusy}
              />
            )}
            <BulkBarButton
              onClick={onBulkExport}
              icon={Download}
              label="Exportar CSV"
              testid="sales-bulk-bar-export"
              disabled={bulkBusy}
            />
          </>
        }
      />
      {confirmDialog}

      <section className="border border-zinc-200 hidden lg:block">
        <div className="grid grid-cols-12 px-4 py-3 bg-zinc-50 border-b border-zinc-200 text-[11px] uppercase tracking-[0.2em] font-medium text-muted2 items-center">
          <div className="col-span-3 flex items-center gap-3">
            <SelectAllCheckbox
              allSelected={selection.allSelected}
              someSelected={selection.someSelected}
              onChange={selection.toggleAll}
              testid="sales-select-all"
            />
            <span>Producto</span>
          </div>
          <div className="col-span-3">Cliente</div>
          <div className="col-span-1 text-right">Cant.</div>
          <div className="col-span-2 text-right">Total</div>
          <div className="col-span-2">Estado</div>
          <div className="col-span-1 text-right">Acción</div>
        </div>
        {filtered.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted2">
            <Receipt size={28} className="mx-auto mb-3 text-zinc-400" />
            No hay pedidos para mostrar.
          </div>
        )}
        {filtered.map((s) => {
          const st = STATES.find((x) => x.value === s.estado) || STATES[0];
          const ChannelIcon = s.canal === "whatsapp" ? WhatsappLogo : s.canal === "web" ? Globe : User;
          return (
            <div
              key={s.id}
              className={`grid grid-cols-12 px-4 py-3 border-b border-zinc-100 text-sm items-center hover:bg-zinc-50 ${selection.isSelected(s.id) ? "bg-[#EEF2FF]" : ""}`}
              data-testid={`sale-row-${s.id}`}
            >
              <div className="col-span-3 min-w-0 flex items-center gap-3">
                <RowCheckbox
                  checked={selection.isSelected(s.id)}
                  onChange={() => selection.toggle(s.id)}
                  testid={`select-sale-${s.id}`}
                />
                <div className="min-w-0">
                  <div className="font-medium truncate">{s.product_nombre || "—"}</div>
                  <div className="text-xs text-muted2 font-mono">{s.id.slice(0, 8)}</div>
                </div>
              </div>
              <div className="col-span-3 min-w-0">
                <div className="font-medium truncate flex items-center gap-1">
                  <ChannelIcon size={14} className="text-muted2 shrink-0" /> {s.comprador_nombre}
                </div>
                <div className="text-xs text-muted2 truncate">
                  {s.comprador_telefono || s.comprador_email || "Sin contacto"}
                </div>
              </div>
              <div className="col-span-1 text-right tabular-nums">{s.cantidad}</div>
              <div className="col-span-2 text-right tabular-nums">
                <div className="font-medium">{fmt(s.total)}</div>
                <div className="text-[11px] text-[#00C853]">+{fmt(s.utilidad)}</div>
              </div>
              <div className="col-span-2">
                <select
                  value={s.estado}
                  onChange={(e) => update(s.id, e.target.value)}
                  className="text-xs border px-2 py-1 font-medium"
                  style={{ borderColor: st.color, color: st.color, background: st.bg }}
                  data-testid={`status-${s.id}`}
                >
                  {STATES.map((x) => (
                    <option key={x.value} value={x.value}>
                      {x.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-1 flex justify-end">
                <button
                  onClick={() => onDelete(s.id)}
                  className="p-1.5 hover:bg-zinc-100 text-[#FF2A00]"
                  data-testid={`delete-sale-${s.id}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </section>

      {/* Mobile / tablet card list */}
      <section className="lg:hidden space-y-3" data-testid="sales-cards">
        {filtered.length === 0 && (
          <div className="border border-zinc-200 px-4 py-12 text-center text-sm text-muted2">
            <Receipt size={28} className="mx-auto mb-3 text-zinc-400" />
            No hay pedidos para mostrar.
          </div>
        )}
        {filtered.map((s) => {
          const st = STATES.find((x) => x.value === s.estado) || STATES[0];
          const ChannelIcon = s.canal === "whatsapp" ? WhatsappLogo : s.canal === "web" ? Globe : User;
          return (
            <div
              key={s.id}
              className={`border border-zinc-200 p-3 ${selection.isSelected(s.id) ? "bg-[#EEF2FF]" : ""}`}
              data-testid={`sale-card-${s.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="shrink-0 pt-0.5">
                  <RowCheckbox
                    checked={selection.isSelected(s.id)}
                    onChange={() => selection.toggle(s.id)}
                    testid={`m-select-sale-${s.id}`}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{s.product_nombre || "—"}</div>
                  <div className="text-[10px] text-muted2 font-mono">{s.id.slice(0, 8)}</div>
                </div>
                <button
                  onClick={() => onDelete(s.id)}
                  className="p-1.5 hover:bg-zinc-100 text-[#FF2A00] shrink-0"
                  data-testid={`m-delete-sale-${s.id}`}
                >
                  <Trash size={14} />
                </button>
              </div>
              <div className="flex items-center gap-1 mt-1.5 text-xs">
                <ChannelIcon size={12} className="text-muted2 shrink-0" />
                <span className="font-medium truncate">{s.comprador_nombre}</span>
                <span className="text-muted2 truncate">· {s.comprador_telefono || s.comprador_email || "Sin contacto"}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div>
                  <div className="overline">Cant.</div>
                  <div className="tabular-nums font-medium">{s.cantidad}</div>
                </div>
                <div>
                  <div className="overline">Total</div>
                  <div className="tabular-nums font-medium">{fmt(s.total)}</div>
                  <div className="text-[10px] text-[#00C853]">+{fmt(s.utilidad)}</div>
                </div>
                <div>
                  <div className="overline">Estado</div>
                  <select
                    value={s.estado}
                    onChange={(e) => update(s.id, e.target.value)}
                    className="text-[11px] border px-1.5 py-0.5 font-medium w-full"
                    style={{ borderColor: st.color, color: st.color, background: st.bg }}
                    data-testid={`m-status-${s.id}`}
                  >
                    {STATES.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {stats?.top_productos?.length > 0 && (
        <section className="mt-10 border border-zinc-200 p-6">
          <div className="overline">Top productos vendidos</div>
          <div className="h-display text-2xl font-bold mt-1 mb-4">Bestsellers</div>
          <ul className="divide-y divide-zinc-100">
            {stats.top_productos.map((p, i) => (
              <li key={p.product_id} className="py-3 flex items-center gap-4">
                <div className="font-mono text-muted2 w-6 text-right">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{p.nombre || "—"}</div>
                  <div className="text-xs text-muted2">{p.unidades} unidades</div>
                </div>
                <div className="text-right tabular-nums">
                  <div className="font-bold">{fmt(p.total)}</div>
                  <div className="text-xs text-[#00C853]">+{fmt(p.utilidad)}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, accent, positive }) {
  return (
    <div className="border-r border-b border-zinc-200 p-6">
      <div className="overline">{label}</div>
      <div
        className={`h-display text-3xl lg:text-4xl font-black tracking-tighter mt-3 ${
          accent ? "text-klein" : positive === false ? "text-[#FF2A00]" : ""
        }`}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-muted2 mt-2">{sub}</div>}
    </div>
  );
}
